/**
 * ChapterMasteryRow — one row in the Padronanza chapter list (design spec
 * § "Layout WEB"): number, title, Meter, coverage, state badge; expands to a
 * per-sub-theme breakdown with a "Ripassa" action per sub-theme.
 */

import * as React from 'react'
import { clsx } from 'clsx'
import type { ChapterMastery, MasteryState, SubThemeMastery } from '@arianna/reader-core'
import Card from '@arianna/design-system/Card'
import Badge from '@arianna/design-system/Badge'
import Meter from '@arianna/design-system/Meter'

// ---------------------------------------------------------------------------
// State -> copy / Badge variant (design spec "Copy" section — exact strings)
// ---------------------------------------------------------------------------

const STATE_LABEL: Record<MasteryState, string> = {
  strong: 'Solido',
  consolidate: 'Da consolidare',
  weak: 'Fragile',
  insufficient: 'Ancora pochi dati',
}

// Badge's `variant` prop type isn't reachable from the `.web.tsx` entry this
// package's tsconfig path aliases resolve to (only Badge/index.ts re-exports
// BadgeVariantProps) — mirroring the variant union literally here avoids a
// fragile cross-package type import for four fixed string values.
type BadgeVariant = 'accent' | 'gold' | 'green' | 'red' | 'neutral'

const STATE_BADGE_VARIANT: Record<MasteryState, BadgeVariant> = {
  strong: 'green',
  consolidate: 'gold',
  weak: 'red',
  insufficient: 'neutral',
}

function reliabilityNote(answered: number): string {
  return `Solo ${answered} risposte — indicativo`
}

// ---------------------------------------------------------------------------
// Sub-theme row (rendered inside the expanded chapter)
// ---------------------------------------------------------------------------

interface SubThemeRowProps {
  subTheme: SubThemeMastery
  onReview: (subParaId: string) => void
}

function SubThemeRow({ subTheme, onReview }: SubThemeRowProps) {
  const provisional = subTheme.answered > 0 && !subTheme.reliable
  return (
    <div className="flex flex-col gap-1.5 py-2.5 px-3 border-t border-line first:border-t-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-ink font-medium truncate">{subTheme.title}</span>
        <Badge variant={STATE_BADGE_VARIANT[subTheme.state]}>{STATE_LABEL[subTheme.state]}</Badge>
      </div>
      <div className="flex items-center gap-3">
        <Meter
          value={subTheme.masteryPct}
          state={subTheme.state}
          provisional={provisional}
          size="sm"
          label={`Padronanza ${subTheme.title}`}
          className="flex-1"
        />
        <span className="text-xs text-ink-3 font-mono tabular-nums shrink-0">
          {subTheme.answered}/{subTheme.questionsTotal} risposte
        </span>
        {subTheme.reviewSubParaId != null && (
          <button
            type="button"
            onClick={() => onReview(subTheme.reviewSubParaId!)}
            className="shrink-0 text-xs font-semibold text-accent bg-transparent border-none cursor-pointer px-1 py-0.5 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
          >
            Ripassa →
          </button>
        )}
      </div>
      {provisional && (
        <div className="text-[11px] text-ink-3 italic">{reliabilityNote(subTheme.answered)}</div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// ChapterMasteryRow
// ---------------------------------------------------------------------------

export interface ChapterMasteryRowProps {
  chapter: ChapterMastery
  expanded: boolean
  onToggle: (chapterId: string) => void
  onReview: (subParaId: string) => void
  /** "Mettiti alla prova" — start a chapter-focused practice session (fragile mode). */
  onStartChapterPractice: (chapterId: string) => void
  /** DOM id assigned to the row root, so the Spectrum can scroll to it. */
  anchorId: string
}

export function ChapterMasteryRow({
  chapter,
  expanded,
  onToggle,
  onReview,
  onStartChapterPractice,
  anchorId,
}: ChapterMasteryRowProps) {
  const provisional = chapter.answered > 0 && !chapter.reliable
  const pct = Math.round(chapter.masteryPct * 100)

  return (
    <Card
      id={anchorId}
      padding="md"
      className={clsx('flex flex-col gap-3 scroll-mt-4', provisional && 'opacity-[0.72]')}
    >
      <button
        type="button"
        onClick={() => onToggle(chapter.chapterId)}
        aria-expanded={expanded}
        className="flex items-center gap-3 w-full bg-transparent border-none p-0 cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 rounded"
      >
        <span className="font-mono text-sm text-ink-3 tabular-nums shrink-0 w-6 text-right">
          {chapter.chapterId}
        </span>
        <span className="flex-1 min-w-0 text-[15px] font-medium text-ink truncate font-serif">
          {chapter.title}
        </span>
        <Badge variant={STATE_BADGE_VARIANT[chapter.state]}>{STATE_LABEL[chapter.state]}</Badge>
        <span
          aria-hidden="true"
          className={clsx(
            'shrink-0 text-ink-3 text-xs transition-transform',
            'motion-reduce:transition-none',
            expanded && 'rotate-180',
          )}
        >
          ▾
        </span>
      </button>

      <div className="flex items-center gap-3 pl-9">
        <Meter
          value={chapter.masteryPct}
          state={chapter.state}
          provisional={provisional}
          label={`Padronanza ${chapter.title}: ${pct}%`}
          className="flex-1"
        />
        <span className="text-xs text-ink-3 font-mono tabular-nums shrink-0">
          {chapter.answered}/{chapter.questionsTotal} risposte
        </span>
      </div>

      {provisional && (
        <div className="pl-9 text-[11px] text-ink-3 italic">
          {reliabilityNote(chapter.answered)}
        </div>
      )}

      {expanded && (
        <div className="ml-9 flex flex-col gap-2">
          {/* "Mettiti alla prova" — hidden when the chapter has no quiz questions
           *  at all (nothing for the sampler to draw from). */}
          {chapter.questionsTotal > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onStartChapterPractice(chapter.chapterId)}
                className="shrink-0 text-xs font-semibold text-accent bg-transparent border-none cursor-pointer px-1 py-0.5 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
              >
                Mettiti alla prova →
              </button>
            </div>
          )}
          <div className="rounded-md border border-line bg-surface overflow-hidden">
            {chapter.subThemes.map((subTheme) => (
              <SubThemeRow key={subTheme.subThemeId} subTheme={subTheme} onReview={onReview} />
            ))}
            {chapter.subThemes.length === 0 && (
              <div className="py-2.5 px-3 text-xs text-ink-3">
                Nessun sotto-tema con domande in questo capitolo.
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}
